import React from 'react';
import type { StepType } from '@reactour/tour';
import { useUserRole } from '../../hooks/useUserRole';
import { LYW_BRAND } from '../constants/brandPalette';

/** LYW tour step; extends @reactour/tour with optional pointer overlay control. */
export type LywTourStep = StepType & {
  /** When true, keep spotlight on the target but omit the sketchy popover→target arrow. */
  hidePopoverPointer?: boolean;
};

/**
 * @reactour/tour steps for the LYW onboarding tour.
 * Targets use data-tour attributes: profiles, my-plan, reports (nav);
 * tab-add-topics, tab-review, tab-inprogress, tab-completed (Lessons page tabs).
 */
export const LYW_ONBOARDING_TOUR_STORAGE_KEY = 'lyw_onboarding_tour_completed';

/** User-specific storage key so tour completion is per-user, not per-browser. */
export function getLywTourStorageKeyForUser(userId: string | null | undefined): string {
  return userId ? `${LYW_ONBOARDING_TOUR_STORAGE_KEY}_${userId}` : LYW_ONBOARDING_TOUR_STORAGE_KEY;
}

/** Hook returning the tour storage key for the current user. */
export function useLywTourStorageKey(): string {
  const { userId } = useUserRole();
  return getLywTourStorageKeyForUser(userId);
}

/** After the tour ends (finish or skip), return the user to the Dashboard. */
export function navigateToDashboardAfterTour(): void {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash.replace(/^#/, '');
  if (!/^\/app\/?$/.test(hash)) {
    window.location.hash = '#/app';
  }
}

/** Position: centered horizontally, near top of viewport */
const POSITION_CENTER_TOP = ({
  windowWidth,
  width: helperWidth,
}: {
  windowWidth: number;
  width: number;
}) => [windowWidth / 2 - helperWidth / 2, 24] as [number, number];

/** Brand colors for tour styling */
const LYW_COLORS = {
  primary: LYW_BRAND.blumine,
  accent: LYW_BRAND.vibrantGreen,
  text: LYW_BRAND.abbey,
  textStrong: LYW_BRAND.blumine,
} as const;

const TourStepContent = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ fontFamily: "'Lato', sans-serif" }}>
    <h3
      style={{
        color: LYW_COLORS.textStrong,
        fontSize: 18,
        fontWeight: 700,
        marginBottom: 10,
        letterSpacing: '0.02em',
      }}
    >
      {title}
    </h3>
    <div style={{ color: '#000', fontSize: 15, lineHeight: 1.5, fontWeight: 400, padding: '4px 0' }}>
      {children}
    </div>
  </div>
);

/**
 * Creates tour steps for the LYW onboarding tour.
 * Help opens as a split-pane drawer via HelpChatProvider (not a separate route).
 */
export function createLywOnboardingTourSteps(): LywTourStep[] {
  return [
    {
      selector: '[data-tour="help-agent"]',
      content: (
        <TourStepContent title="Your Help Agent">
          Click here anytime to chat with Alex for assistance. Get help with setup, curriculum, and using the app.
        </TourStepContent>
      ),
      position: 'top',
    },
    {
      selector: '[data-tour="dashboard"]',
      content: (
        <TourStepContent title="Your Dashboard">
          The Dashboard is where you see actions that need doing and access the Control Panel to change account settings and logout.
        </TourStepContent>
      ),
      position: POSITION_CENTER_TOP,
    },
    {
      selector: '[data-tour="profiles"]',
      content: (
        <TourStepContent title="Complete Your Profiles">
          Setup your Learning Environment and add your Students
        </TourStepContent>
      ),
      position: POSITION_CENTER_TOP,
    },
    {
      selector: '[data-tour="my-plan"]',
      content: (
        <TourStepContent title="Plan">
          This is where you add curriculum topics and create units.
        </TourStepContent>
      ),
      action: () => {
        const hash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
        if (!hash.startsWith('/app/lessons')) {
          setTimeout(() => {
            window.location.hash = '#/app/lessons';
          }, 150);
        }
      },
      position: POSITION_CENTER_TOP,
      mutationObservables: ['main'],
    },
    {
      selector: '[data-tour="tab-add-topics"]',
      content: (
        <TourStepContent title="Add Topics">
          Use Add Topics to browse the curriculum library and copy topics to the student&apos;s plan, or use Create Your Own Topic(s) to pick or create a custom subject and add catalog topics in one guided flow.
        </TourStepContent>
      ),
      action: () => {
        const hash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
        if (!hash.startsWith('/app/lessons/add-topics')) {
          setTimeout(() => {
            window.location.hash = '#/app/lessons/add-topics';
          }, 150);
        }
      },
      position: 'center',
      mutationObservables: ['main'],
    },
    {
      selector: '[data-tour="tab-create-units"]',
      content: (
        <TourStepContent title="Create Units">
          On the Create Units tab, use Create Unit to choose a single-subject or blended unit from that student&apos;s curriculum.
        </TourStepContent>
      ),
      action: () => {
        const hash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
        if (!hash.startsWith('/app/lessons/create')) {
          setTimeout(() => {
            window.location.hash = '#/app/lessons/create';
          }, 150);
        }
      },
      position: 'center',
      mutationObservables: ['main'],
    },
    {
      selector: '[data-tour="tab-review"]',
      content: (
        <TourStepContent title="Approve Units">
          Review and approve the lessons created by our AI in your learning unit
        </TourStepContent>
      ),
      position: 'center',
      mutationObservables: ['main'],
    },
    {
      selector: '[data-tour="tab-inprogress"]',
      content: (
        <TourStepContent title="Teach!">
          Use the lessons to teach your chosen topics together
        </TourStepContent>
      ),
      position: 'center',
      mutationObservables: ['main'],
    },
    {
      selector: '[data-tour="tab-inprogress"]',
      content: (
        <TourStepContent title="Optional Assessment">
          After completing all lessons, our AI can generate an assessment for your student
        </TourStepContent>
      ),
      position: 'center',
      mutationObservables: ['main'],
      hidePopoverPointer: true,
    },
    {
      selector: '[data-tour="tab-completed"]',
      content: (
        <TourStepContent title="Complete Unit">
          Once you are all done, finish the unit and mark Learning Objectives achieved
        </TourStepContent>
      ),
      position: 'center',
      mutationObservables: ['main'],
    },
    {
      selector: '[data-tour="reports"]',
      content: (
        <TourStepContent title="Check your Progress">
          Plan gives you a quick view of each topic&apos;s status. For richer progress and analytics, go to Reports.
        </TourStepContent>
      ),
      position: 'center',
      mutationObservables: ['main'],
    },
  ];
}

export const lywOnboardingTourStyles = {
  popover: (base: Record<string, unknown>) => ({
    ...base,
    borderRadius: 12,
    padding: '20px 24px',
    fontFamily: "'Lato', sans-serif",
    border: `2px solid ${LYW_BRAND.blumine}`,
    boxShadow: 'none',
    maxWidth: 380,
  }),
  badge: (base: Record<string, unknown>) => ({
    ...base,
    backgroundColor: LYW_COLORS.accent,
  }),
  close: (base: Record<string, unknown>) => ({
    ...base,
    color: LYW_COLORS.text,
  }),
  button: (base: Record<string, unknown>) => ({
    ...base,
    cursor: 'pointer',
  }),
  arrow: (base: Record<string, unknown>, state: { disabled?: boolean }) => ({
    ...base,
    color: state.disabled ? '#caccce' : LYW_COLORS.primary,
    width: 18,
    height: 14,
    flex: '0 0 18px',
  }),
  maskArea: (base: Record<string, unknown>) => ({
    ...base,
    rx: 0,
  }),
  maskRect: (base: Record<string, unknown>) => ({
    ...base,
    fill: 'transparent',
  }),
};
